import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Avatar } from "@/components/common";
import DataTable from "@/components/DataTable";
import { Input } from "@/components/ui/input";
import { Trophy } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const PERIODS = [["month", "Month"], ["overall", "Overall"], ["3m", "Last 3 months"], ["6m", "Last 6 months"], ["ay", "Academic Year"], ["fy", "Financial Year"]];

function TargetInput({ row, month, onSaved }) {
  const [v, setV] = useState(row.target || "");
  const save = async () => {
    if (String(v) === String(row.target || "")) return;
    try { await api.post("/targets", { counselor_id: row.counselor_id, month, target: Number(v) || 0 }); toast.success(`Target set for ${row.name}`); onSaved(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return <Input type="number" min="0" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()} className="w-20 h-8" data-testid={`target-input-${row.counselor_id}`} />;
}

export default function Performance() {
  const { user } = useAuth();
  const isMgr = ["super_admin", "admin", "admission_manager", "management"].includes(user.role);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [period, setPeriod] = useState("month");
  const [d, setD] = useState(null);
  const load = () => api.get(`/performance?period=${period}&month=${month}`).then(({ data }) => setD(data));
  useEffect(() => { setD(null); load(); }, [month, period]); // eslint-disable-line
  const columns = [
    { key: "rank", label: "#", render: (r) => <span className="font-heading font-bold text-[#312E81]">{r.rank}</span> },
    { key: "name", label: "Counselor", render: (r) => <div className="flex items-center gap-2"><Avatar name={r.name} size={28} /><div><div className="font-semibold">{r.name}</div><div className="text-[11px] text-slate-400">{r.designation}</div></div></div> },
    { key: "admissions", label: "Admissions", render: (r) => <span className="font-bold text-lg">{r.admissions}</span> },
    { key: "target", label: "Target", render: (r) => (isMgr && period === "month" ? <TargetInput key={`${r.counselor_id}-${month}`} row={r} month={month} onSaved={load} /> : r.target || "—") },
    { key: "pct", label: "Progress", render: (r) => r.pct == null ? <span className="text-xs text-slate-400">No target</span> : (
      <div className="w-32"><div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#4F46E5]" style={{ width: `${Math.min(r.pct, 100)}%` }} /></div><div className="text-[11px] text-slate-500 mt-0.5">{r.pct}%</div></div>) },
  ];
  return (
    <div>
      <PageHeader title="Performance & Targets" subtitle={d?.label && period !== "month" ? `Admissions vs targets · ${d.label}` : "Monthly admissions vs targets"} testid="performance-header">
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger className="w-44 bg-white" data-testid="perf-period"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">{PERIODS.map(([v, l]) => <SelectItem key={v} value={v} data-testid={`perf-period-${v}`}>{l}</SelectItem>)}</SelectContent>
        </Select>
        {period === "month" && <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44 bg-white" data-testid="perf-month" />}
      </PageHeader>
      <div className="grid sm:grid-cols-3 gap-3 mb-4" data-testid="perf-top">
        {(d?.top || []).map((t, i) => (
          <div key={i} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
            <Trophy className={`w-6 h-6 ${["text-amber-500", "text-slate-400", "text-orange-600"][i]}`} />
            <div className="flex-1"><div className="font-semibold">{t.name}</div><div className="text-xs text-slate-400">#{i + 1} {period === "month" ? "this month" : d?.label}</div></div>
            <div className="text-2xl font-heading font-extrabold text-[#312E81]">{t.admissions}</div>
          </div>
        ))}
      </div>
      <DataTable columns={columns} rows={d?.rows ?? null} loading={!d} testid="perf-table" rowTestid="perf-row" />
    </div>
  );
}
